import React, { useState } from 'react'
import { Button, Card, Collapse, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { DMSConfig } from '../../../../services/config.service'
import { InputSwitch } from '../../../common/InputSwitch'

export interface ESignatureCredentialsCardProps {
  configFormData: DMSConfig | Partial<DMSConfig>
  disabled: boolean
  configLoading: boolean
  onRevealConfig: () => void
  onInputChange: (key: string, value: unknown) => void
  children?: React.ReactNode
}

export const ESignatureCredentialsCard: React.FC<ESignatureCredentialsCardProps> = ({
  configFormData,
  disabled,
  configLoading,
  onRevealConfig,
  onInputChange,
  children,
}) => {
  const { t } = useTranslation()
  const [instructionsOpen, setInstructionsOpen] = useState(false)

  const maskValue = (set: boolean | undefined, value: string | null | undefined) =>
    set && (value === undefined || value === null) ? '******' : (value ?? '')
  const readOnly = (set: boolean | undefined, value: string | null | undefined) => !!(set && (value === undefined || value === null))

  return (
    <Card className="mb-4">
      <Card.Body>
        <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-1 flex-grow-1">{t('docusign.title')}</div>
          <span className="d-inline-flex align-items-center" title={t('docusign.settings.showConfigTooltip')}>
            <Button
              type="button"
              variant="link"
              className="p-0 text-secondary"
              size="sm"
              disabled={disabled || configLoading}
              onClick={onRevealConfig}
              aria-label={t('docusign.settings.showConfigTooltip')}
            >
              {configLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <i className="bi bi-arrow-clockwise" aria-hidden="true" />
              )}
            </Button>
          </span>
        </Card.Title>
        <p className="text-muted small mb-2">{t('docusign.settings.help')}</p>
        {/* Trap browser password-manager autofill: it targets the first text + password pair in a form. */}
        <div
          className="position-absolute overflow-hidden"
          style={{ width: 1, height: 1, left: -9999, top: 0, clipPath: 'inset(50%)' }}
          aria-hidden="true"
        >
          <input type="text" name="docusign_autofill_trap_user" tabIndex={-1} autoComplete="username" readOnly />
          <input type="password" name="docusign_autofill_trap_pass" tabIndex={-1} autoComplete="current-password" readOnly />
        </div>
        <div className="mb-3">
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => setInstructionsOpen((o) => !o)}
            aria-expanded={instructionsOpen}
          >
            <i className={`bi bi-chevron-${instructionsOpen ? 'up' : 'down'} me-1`} aria-hidden="true" />
            {t('docusign.settings.instructionsTitle')}
          </Button>
          <Collapse in={instructionsOpen}>
            <div className="mt-2 small text-muted">
              <ol className="mb-0 ps-3">
                <li className="mb-1">{t('docusign.settings.instructionsStep1')}</li>
                <li className="mb-1">{t('docusign.settings.instructionsStep2')}</li>
                <li className="mb-1">{t('docusign.settings.instructionsStep3')}</li>
                <li className="mb-1">{t('docusign.settings.instructionsStep4')}</li>
                <li className="mb-1">{t('docusign.settings.instructionsStep5')}</li>
              </ol>
            </div>
          </Collapse>
        </div>
        <Form.Group className="mb-2">
          <InputSwitch
            value={!!configFormData.docusign_enabled}
            onChange={(value) => onInputChange('docusign_enabled', value)}
            title={configFormData.docusign_enabled ? t('enabled') : t('disabled')}
            disabled={disabled}
          />
          <Form.Text className="text-muted">{t('docusign.settings.enableHelp')}</Form.Text>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>{t('docusign.settings.integrationKey')}</Form.Label>
          <Form.Control
            type="text"
            name="docusign_integration_key"
            placeholder={t('docusign.settings.integrationKeyPlaceholder')}
            value={maskValue(configFormData.docusign_integration_key_set, configFormData.docusign_integration_key)}
            onChange={(e) => onInputChange('docusign_integration_key', e.target.value || null)}
            readOnly={readOnly(configFormData.docusign_integration_key_set, configFormData.docusign_integration_key)}
            disabled={disabled}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore
          />
          <Form.Text className="text-muted">{t('docusign.settings.integrationKeyHelp')}</Form.Text>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>{t('docusign.settings.secretKey')}</Form.Label>
          <Form.Control
            type={configFormData.docusign_secret_key ? 'text' : 'password'}
            name="docusign_secret_key"
            placeholder={t('docusign.settings.secretKeyPlaceholder')}
            value={maskValue(configFormData.docusign_secret_key_set, configFormData.docusign_secret_key)}
            onChange={(e) => onInputChange('docusign_secret_key', e.target.value || null)}
            readOnly={readOnly(configFormData.docusign_secret_key_set, configFormData.docusign_secret_key)}
            disabled={disabled}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore
          />
          <Form.Text className="text-muted">{t('docusign.settings.secretKeyHelp')}</Form.Text>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>{t('docusign.settings.baseUrl')}</Form.Label>
          <Form.Select
            value={configFormData.docusign_base_url ?? 'demo.docusign.net'}
            onChange={(e) => onInputChange('docusign_base_url', e.target.value || null)}
            disabled={disabled}
          >
            <option value="demo.docusign.net">{t('docusign.settings.baseUrlDemo')}</option>
            <option value="account.docusign.com">{t('docusign.settings.baseUrlProduction')}</option>
          </Form.Select>
          <Form.Text className="text-muted">{t('docusign.settings.baseUrlHelp')}</Form.Text>
        </Form.Group>
        {children}
      </Card.Body>
    </Card>
  )
}
